import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import {
  ArrowDown,
  ArrowRight,
  Book,
  BookOpen,
  Calendar,
  CalendarClock,
  Gift,
  ImageIcon,
  Layers,
  LayoutGrid,
  LogIn,
  MapPin,
  Quote,
  Ruler,
} from 'lucide-react';
import { CloudinaryPicture, getCloudinaryUrl } from 'components/rich';
import Icon, { ICON_TYPE } from 'components/icon';
import { prepareAncestryAuthUrl } from 'ecommerce/components/signInWithAncestry';
import useUserContext from 'ecommerce/context/user';
import useDataContext from 'data/context/data';

import { Head, Layout } from '../components/layout';
import styles from '../components/ancestry/ancestry.module.scss';
import { christmasDeadlines, formatDate } from '../lib/ancestryLanding.mjs';
import tracker from '../lib/tracker';

const POST_LOGIN_DESTINATION = '/ancestry-family-history';
const IMAGE_PREFIX = 'products/';
const IMAGE_WIDTHS = [320, 480, 640];
// Square photos are cover-cropped into taller slots, so the slot height decides the width needed
const MOSAIC_SIDE_SIZES = '(min-width: 992px) 282px, 144px';
const HERO_IMAGE = 'ancestry-book/5';
const LOGIN_NOTE = 'No new password. Your Ancestry login works here.';

// "FROM $X" uses the cheapest SKU: the 11x8.5 book covers, the smallest poster, the single
// calendar and Family Flip SKUs.
const BOOK = {
  id: 'book',
  name: 'Ancestry Book',
  href: '/ancestry-book',
  imageKey: 'ancestry-book/7',
  fromSkus: [
    'fh-standard-hardcover_book-11x8-leather-black',
    'fh-standard-hardcover_book-11x8-leather-tan',
    'fh-standard-hardcover_book-11x8-custom',
  ],
  hook: 'Years of research, finally something you can hold. Family group sheets, pedigree charts and timelines, with the photos from your tree.',
  specs: [
    { Icon: Layers, label: '20 pages included, add up to 350' },
    { Icon: Ruler, label: '11x8.5 in, or 13x10 in with 40% larger pages' },
    { Icon: Book, label: 'Black or Tan vegan leather, or your own photo' },
  ],
  linkLabel: 'See sizes & options',
};

const PAIR = [
  {
    id: 'poster',
    name: 'Ancestry Poster',
    href: '/ancestry-poster',
    imageKey: 'ancestry-poster/2',
    fromSkus: ['fhp-20x16-4g-combo'],
    hook: 'One wall, every name. Choose a Standard, Combination, Descendant or Fan tree.',
    chips: ['Up to 9 generations', '16x20 to 36x48 in'],
    linkLabel: 'See sizes & options',
  },
  {
    id: 'calendar',
    name: 'Ancestry Calendar',
    href: '/ancestry-calendar',
    imageKey: 'ancestry-calendar/4',
    fromSkus: ['fh-calendar'],
    hook: 'A year of birthdays and anniversaries, with the faces that go with them. It starts any month you choose.',
    chips: ['12 months', '11x17 in hanging', 'Extra copies for family'],
    linkLabel: 'See details',
  },
];

const FAMILY_FLIP_SKUS = ['pexeso'];

const FAMILY_FLIP_FACTS = [
  { value: '20 to 32', label: 'matching pairs' },
  { value: 'Ages 4+', label: 'grandkids to grandparents' },
  { value: 'Free', label: 'online version to share' },
];

const DEADLINE_ICONS = {
  book: BookOpen,
  calendar: Calendar,
  'family-flip': LayoutGrid,
  poster: ImageIcon,
};

const minPrice = (skuProducts) => {
  if (!skuProducts.length) return null;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: skuProducts[0].currency,
    maximumFractionDigits: 0,
  }).format(Math.min(...skuProducts.map((sku) => sku.price)));
};

const trackKeepsakeClick = (product) => () =>
  tracker.onCustomEvent('ancestry_landing_keepsake_click', { product });

/**
 * Ancestry login on the new design layer. Signed-in visitors go straight to the
 * family history hub instead.
 */
const AncestryButton = ({ isLoggedIn, location, className }) => {
  // The OAuth URL sits in href so new-tab clicks keep the flow and the return destination.
  const href = isLoggedIn ? POST_LOGIN_DESTINATION : prepareAncestryAuthUrl(POST_LOGIN_DESTINATION);
  const track = () => tracker.onCustomEvent('ancestry_landing_cta_click', { location });

  return (
    <a
      href={href}
      className={`${styles.ancestryButton} ${className ?? ''}`}
      onClick={track}
      // Middle-click opens a new tab without a click event, right-click (button 2) is ignored
      onAuxClick={(event) => event.button === 1 && track()}
    >
      {isLoggedIn ? (
        <>
          Get started
          <ArrowRight size={22} aria-hidden="true" />
        </>
      ) : (
        <>
          <LogIn size={22} aria-hidden="true" />
          Login with Ancestry
        </>
      )}
    </a>
  );
};

AncestryButton.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  location: PropTypes.oneOf(['hero', 'closing']).isRequired,
  className: PropTypes.string,
};

const PrimaryLink = ({ href, label, onClick }) => (
  <Link href={href} className={styles.primaryButton} onClick={onClick}>
    {label}
    <ArrowRight size={20} aria-hidden="true" />
  </Link>
);

PrimaryLink.propTypes = {
  href: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

const HolidayStrip = ({ year, date }) => (
  <aside className={styles.strip} aria-label={`Christmas ${year} order-by date`}>
    <CalendarClock size={20} className={styles.stripIcon} aria-hidden="true" />
    <p className={styles.stripText}>
      <span className={styles.stripMessage}>
        <span className={styles.desktopOnly}>
          {`Christmas ${year}: order your Ancestry Book by ${formatDate(date, { month: 'long', day: 'numeric' })}`}
        </span>
        <span className={styles.mobileOnly}>
          {`Order your Ancestry Book by ${formatDate(date, { month: 'short', day: 'numeric' })}`}
        </span>
        {' for delivery before Christmas.'}
      </span>{' '}
      <a href="#christmas-deadlines" className={styles.stripLink}>
        See all order-by dates
        <ArrowDown size={16} aria-hidden="true" />
      </a>
    </p>
  </aside>
);

HolidayStrip.propTypes = {
  year: PropTypes.number.isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
};

const DateTile = ({ date }) => (
  <span className={styles.dateTile}>
    <span className={styles.dateMonth} aria-hidden="true">
      {formatDate(date, { month: 'short' })}
    </span>
    <span className={styles.dateDay} aria-hidden="true">
      {date.getUTCDate()}
    </span>
    <span className={styles.visuallyHidden}>
      {formatDate(date, { month: 'long', day: 'numeric' })}
    </span>
  </span>
);

DateTile.propTypes = {
  date: PropTypes.instanceOf(Date).isRequired,
};

const ChristmasDeadlines = ({ year, rows }) => (
  <section
    id="christmas-deadlines"
    className={styles.deadlines}
    aria-labelledby="ancestry-deadlines-title"
  >
    <div className={`${styles.container} ${styles.deadlinesGrid}`}>
      <div className={styles.deadlinesIntro}>
        <p className={styles.eyebrowInverse}>{`Christmas ${year}`}</p>
        <h2 id="ancestry-deadlines-title" className={styles.sectionTitleInverse}>
          Order by these dates for delivery before Christmas.
        </h2>
        <p className={styles.deadlinesBody}>
          The book needs the most time. Every family history book gets a manual quality check before
          it ships. Start with the book, then add the rest.
        </p>
      </div>

      <div className={styles.datesCard}>
        <table className={styles.datesTable}>
          <caption className={styles.datesCaption}>Order by, Standard shipping</caption>
          <thead>
            <tr>
              <th scope="col">Keepsake</th>
              <th scope="col">Standard shipping</th>
              <th scope="col">Express</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ id, name, standard, express }) => {
              const RowIcon = DEADLINE_ICONS[id];
              return (
                <tr key={id} className={styles.datesRow}>
                  <th scope="row" className={styles.datesProduct}>
                    <span className={styles.datesIcon} aria-hidden="true">
                      <RowIcon size={22} />
                    </span>
                    {name}
                  </th>
                  <td className={styles.datesStandard}>
                    <DateTile date={standard} />
                    <span className={styles.datesWeekday}>
                      {formatDate(standard, { weekday: 'long' })}
                      <span className={styles.mobileOnly}>.</span>
                    </span>
                  </td>
                  <td className={styles.datesExpress}>
                    <span className={styles.mobileOnly}>Express by </span>
                    {formatDate(express, { month: 'short', day: 'numeric' })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className={styles.datesFootnote}>
          Estimated order-by dates for the contiguous US. Alaska, Hawaii, Puerto Rico and military
          addresses need extra time.
        </p>
      </div>

      <Link href="/shipping" className={styles.shippingLink}>
        Delivery times and shipping
        <ArrowRight size={20} aria-hidden="true" />
      </Link>
    </div>
  </section>
);

ChristmasDeadlines.propTypes = {
  year: PropTypes.number.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      standard: PropTypes.instanceOf(Date).isRequired,
      express: PropTypes.instanceOf(Date).isRequired,
    }),
  ).isRequired,
};

const AncestryLandingPage = ({ today }) => {
  const { getSkuProducts } = useDataContext();
  const { accessToken } = useUserContext();
  const isLoggedIn = Boolean(accessToken);
  const season = christmasDeadlines(new Date(today));

  const priceOf = (skus) => minPrice(getSkuProducts(skus));
  const familyFlipPrice = priceOf(FAMILY_FLIP_SKUS);

  useEffect(() => {
    tracker.onCustomEvent('ancestry_landing_view', {
      referrer: typeof document !== 'undefined' ? document.referrer : '',
    });
  }, []);

  return (
    <>
      <Head
        title="Turn your Ancestry tree into a keepsake"
        description="Official Ancestry Partner. Sign in with Ancestry and we'll turn your family tree into a printed book, poster, or calendar. Over 200,000 books delivered."
        heroImage={getCloudinaryUrl({ imageKey: HERO_IMAGE, prefix: IMAGE_PREFIX, type: 'big' })}
      />

      {/* In season the holiday strip takes the site banner's place */}
      <Layout showTopBanner={!season}>
        <div className={styles.page}>
          {season && <HolidayStrip year={season.year} date={season.book.standard} />}

          <section className={styles.hero} aria-labelledby="ancestry-hero-title">
            <div className={`${styles.container} ${styles.heroGrid}`}>
              <div className={styles.heroIntro}>
                <p className={styles.partnerLine}>
                  <span className={styles.partnerMark} aria-hidden="true">
                    <Icon type={ICON_TYPE.ANCESTRY} size={13} />
                  </span>
                  <span className={styles.eyebrow}>Official Ancestry Partner</span>
                </p>
                <h1 id="ancestry-hero-title" className={styles.heroTitle}>
                  Your family&apos;s story, printed to last.
                </h1>
                <p className={styles.lead}>
                  You&apos;ve done the research. Now give it a place on the shelf, on the wall and
                  at the holiday table. Your Ancestry tree flows straight into a book, poster,
                  calendar or card game.
                </p>
              </div>

              <div className={styles.mosaic}>
                <div className={styles.mosaicMain}>
                  <CloudinaryPicture
                    prefix={IMAGE_PREFIX}
                    imageKey={HERO_IMAGE}
                    type="big"
                    className={styles.coverImage}
                    priority
                  />
                </div>
                <div className={styles.mosaicSide}>
                  <CloudinaryPicture
                    prefix={IMAGE_PREFIX}
                    imageKey="ancestry-poster/7"
                    widths={IMAGE_WIDTHS}
                    sizes={MOSAIC_SIDE_SIZES}
                    className={styles.coverImage}
                  />
                  <CloudinaryPicture
                    prefix={IMAGE_PREFIX}
                    imageKey="family-flip/5"
                    widths={IMAGE_WIDTHS}
                    sizes={MOSAIC_SIDE_SIZES}
                    className={styles.coverImage}
                  />
                </div>
              </div>

              <div className={styles.heroActions}>
                <div className={styles.ctaRow}>
                  <AncestryButton isLoggedIn={isLoggedIn} location="hero" />
                  <a href="#keepsakes" className={styles.textLink}>
                    See the keepsakes
                    <ArrowDown size={20} aria-hidden="true" />
                  </a>
                </div>
                {!isLoggedIn && <p className={styles.loginNote}>{LOGIN_NOTE}</p>}
                <ul className={styles.trustRow}>
                  <li>
                    <BookOpen size={20} aria-hidden="true" />
                    Over 200,000 books delivered
                  </li>
                  <li>
                    <MapPin size={20} aria-hidden="true" />
                    Printed in the USA
                  </li>
                </ul>
              </div>
            </div>
          </section>

          <section id="keepsakes" className={styles.keepsakes} aria-labelledby="ancestry-keepsakes">
            <div className={styles.container}>
              <header className={styles.keepsakesHead}>
                <div>
                  <p className={styles.eyebrow}>Keepsakes</p>
                  <h2 id="ancestry-keepsakes" className={styles.sectionTitle}>
                    Three ways to hold your family story.
                  </h2>
                </div>
                <p className={styles.keepsakesIntro}>
                  Each one starts with the tree you already built. Names, dates and photos come
                  across from Ancestry, ready for you to polish.
                </p>
              </header>

              <article className={`${styles.tile} ${styles.tileWide}`}>
                <div className={styles.tilePhoto}>
                  <CloudinaryPicture
                    prefix={IMAGE_PREFIX}
                    imageKey={BOOK.imageKey}
                    type="big"
                    className={styles.coverImage}
                    loading="lazy"
                  />
                </div>
                <div className={styles.tileBody}>
                  {priceOf(BOOK.fromSkus) && (
                    <p className={styles.eyebrow}>{`From ${priceOf(BOOK.fromSkus)}`}</p>
                  )}
                  <h3 className={styles.tileTitleWide}>{BOOK.name}</h3>
                  <p className={styles.tileHook}>{BOOK.hook}</p>
                  <ul className={styles.specs}>
                    {BOOK.specs.map(({ Icon: SpecIcon, label }) => (
                      <li key={label}>
                        <SpecIcon size={20} aria-hidden="true" />
                        {label}
                      </li>
                    ))}
                  </ul>
                  <PrimaryLink
                    href={BOOK.href}
                    label={BOOK.linkLabel}
                    onClick={trackKeepsakeClick(BOOK.id)}
                  />
                </div>
              </article>

              <div className={styles.pair}>
                {PAIR.map((product) => {
                  const price = priceOf(product.fromSkus);
                  return (
                    <article key={product.id} className={styles.tile}>
                      <div className={styles.tilePhoto}>
                        <CloudinaryPicture
                          prefix={IMAGE_PREFIX}
                          imageKey={product.imageKey}
                          type="big"
                          className={styles.coverImage}
                          loading="lazy"
                        />
                      </div>
                      <div className={styles.tileBody}>
                        {price && <p className={styles.eyebrow}>{`From ${price}`}</p>}
                        <h3 className={styles.tileTitle}>{product.name}</h3>
                        <p className={styles.tileHook}>{product.hook}</p>
                        <ul className={styles.chips}>
                          {product.chips.map((chip) => (
                            <li key={chip}>{chip}</li>
                          ))}
                        </ul>
                        <PrimaryLink
                          href={product.href}
                          label={product.linkLabel}
                          onClick={trackKeepsakeClick(product.id)}
                        />
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </section>

          <section className={styles.gift} aria-labelledby="ancestry-family-flip">
            <div className={`${styles.container} ${styles.giftGrid}`}>
              {season && (
                <p className={styles.giftBadge}>
                  <Gift size={18} aria-hidden="true" />
                  Christmas gift idea
                </p>
              )}
              <h2 id="ancestry-family-flip" className={styles.giftTitle}>
                A game night with the whole family tree.
              </h2>
              <div className={styles.giftPhoto}>
                <CloudinaryPicture
                  prefix={IMAGE_PREFIX}
                  imageKey="family-flip/4"
                  type="big"
                  className={styles.coverImage}
                  loading="lazy"
                />
              </div>
              <p className={styles.giftBody}>
                Family Flip is a memory game made from the people in your tree. Every match is a
                face, a name and a story worth telling. Lay it out after the holiday dinner, and
                share the free online version with family who can&apos;t be there.
              </p>
              <dl className={styles.facts}>
                {FAMILY_FLIP_FACTS.map(({ value, label }) => (
                  <div key={value} className={styles.fact}>
                    <dt className={styles.factValue}>{value}</dt>
                    <dd className={styles.factLabel}>{label}</dd>
                  </div>
                ))}
              </dl>
              <div className={styles.priceRow}>
                {familyFlipPrice && (
                  <p className={styles.price}>
                    <span className={styles.pricePrefix}>From</span>
                    <span className={styles.priceValue}>{familyFlipPrice}</span>
                  </p>
                )}
                <PrimaryLink
                  href="/family-flip"
                  label="See Family Flip"
                  onClick={trackKeepsakeClick('family_flip')}
                />
              </div>
            </div>
          </section>

          {season && <ChristmasDeadlines year={season.year} rows={season.rows} />}

          <section className={styles.closing} aria-labelledby="ancestry-closing-title">
            <div className={`${styles.container} ${styles.closingGrid}`}>
              <figure className={styles.quote}>
                <Quote size={48} className={styles.quoteMark} aria-hidden="true" />
                <blockquote className={styles.quoteText}>
                  <p>
                    I have spent the last few years doing the research for this project. I am so
                    excited to be able to share it with my family by giving them each a poster.
                  </p>
                </blockquote>
                <figcaption className={styles.quoteAuthor}>
                  <span className={styles.quoteRule} aria-hidden="true" />
                  Julie, MyCanvas customer
                </figcaption>
              </figure>

              <div className={styles.ctaCard}>
                <h2 id="ancestry-closing-title" className={styles.ctaTitle}>
                  Ready to print the tree you&apos;ve been building?
                </h2>
                <p className={styles.ctaBody}>
                  Sign in and pick a keepsake. Your tree comes across from Ancestry, ready for you
                  to polish and order.
                </p>
                <AncestryButton
                  isLoggedIn={isLoggedIn}
                  location="closing"
                  className={styles.ancestryButtonFull}
                />
                {!isLoggedIn && <p className={styles.loginNote}>{LOGIN_NOTE}</p>}
              </div>
            </div>
          </section>
        </div>
      </Layout>
    </>
  );
};

// Server render per request so the seasonal sections follow today's date (same as /shipping).
AncestryLandingPage.getInitialProps = async () => ({ today: new Date().toISOString() });

AncestryLandingPage.propTypes = {
  today: PropTypes.string.isRequired,
};

export default AncestryLandingPage;
